import { Exclude, Expose, Type } from "class-transformer";
import { IsArray, IsDate, IsDefined, IsNumber, IsOptional, IsString, ValidateNested } from "class-validator";

import { GField, GFloat, GInputType, GObjectType } from "../graphql";
import { TransformToDate } from "../transformers/transformToDate";

@GObjectType("LocationArrival")
@GInputType("LocationArrivalInput")
@Exclude()
export class LocationArrival {
    @GField()
    @IsString()
    @IsOptional()
    public locationId?: string;

    @GField()
    @IsString()
    public locationName: string;

    @GField()
    @IsString()
    public timezone: string;

    /**
     * Arrival time at the location in local time.
     * It is stored as a date suggesting it's in the UTC timezone, but it's actually in the local timezone as part of legacy
     * handling of the timezones.
     */
    @GField(() => Date, { nullable: true })
    @IsDate()
    @Expose()
    @TransformToDate
    public arrivalAt: Date | null;

    /**
     * Departure time at the location in local time.
     * It is stored as a date suggesting it's in the UTC timezone, but it's actually in the local timezone as part of legacy
     * handling of the timezones.
     */
    @GField(() => Date, { nullable: true })
    @IsDate()
    @Expose()
    @TransformToDate
    public departureAt: Date | null;

    @GField(() => GFloat)
    @IsNumber()
    @Expose()
    public distanceKm: number;

    @GField(() => GFloat)
    @IsNumber()
    @Expose()
    public travelTimeMinutes: number;
}

/**
 * TravelEstimates encapsulates calculated details of travel.
 * This includes travel time, distance, arrival time of the whole trip, as well as a list of locations that
 * will be visited during the trip with estimated arrival times at each specified location.
 *
 * Main algorithm for calculation of travel time using travel data is located in:
 * `api/src/domain/content/travel-data/queries/calculate-travel-data/travel-data-algorithm.ts`
 *
 * Main util for calculating arrival times is located in:
 * `api/src/domain/content/travel-estimates/utils/calculate-arrival-times.ts`
 *
 * Legacy service that was the base of this class is located in `@legacy/services/CalculatorService.ts`
 */
@GObjectType("TravelEstimates")
@GInputType("TravelEstimatesInput")
@Exclude()
export class TravelEstimates {
    /**
     * Travel time in minutes - this is the time spent on the road between locations,
     * it does not contain the time spent at locations.
     */
    @GField(() => GFloat)
    @IsNumber()
    @IsDefined()
    @Expose()
    public travelTimeMinutes: number;

    /**
     * Total time spent at locations in minutes - this is the cumulative time spent at all
     * locations during the journey.
     */
    @GField(() => GFloat)
    @IsNumber()
    @IsDefined()
    @Expose()
    public timeAtLocationsMinutes: number;

    /**
     * Total distance travelled in kilometers.
     */
    @GField(() => GFloat)
    @IsNumber()
    @IsDefined()
    @Expose()
    public distanceKm: number;

    /**
     * Estimated arrival time at the destination.
     * Stored as a date suggesting it's in the UTC timezone, but it's actually in the local timezone as part of legacy handling of the timezones.
     * It may be null if the departure time is not known yet.
     */
    @GField(() => Date, { nullable: true })
    @IsOptional()
    @IsDate()
    @Expose()
    @TransformToDate
    public arrivalAt: Date | null;

    /**
     * A list of locations that will be visited during the trip with estimated arrival times and distances from each other.
     * The list is sorted by arrival time.
     */
    @GField(() => [LocationArrival])
    @IsArray()
    @Type(() => LocationArrival)
    @ValidateNested({ each: true })
    @Expose()
    public locationArrivals: LocationArrival[];

    /**
     * Hash of the locations of the order to check if the costly travel estimates need to be recalculated
     * Calculated by `getLocationsArrivalsHash` function
     */
    @GField()
    @IsString()
    public hash: string;
}
